<template>
    <form-wrapper @submit="handleFormSubmission(data)">
        <form-row>
            <form-cell>
                <text-field
                    :label="$t('forms.auth.reset_password.fields.password.label')"
                    :placeholder="$t('forms.auth.reset_password.fields.password.placeholder')"
                    :hasAutofocus="true"
                    :error="inviteError"
                    :isRequired="true"
                    :formData="data"
                    valuePath="password"
                    :type="passwordInputFieldType"
                    :maxLength="40"
                    @change="change($event)"
                >
                    <template #error="{ error }">
                        <p v-if="error === 'error'" class="">
                            {{ $t('pages.auth.reset_password.token_expired') }}
                            <span class="error-button" @click="sendReminder()">
                                <u>{{ $t('pages.auth.reset_password.token_expired_here') }}</u>
                            </span>
                            {{ $t('pages.auth.reset_password.token_expired_resend') }}
                        </p>
                        <p v-else>
                            {{ error }}
                        </p>
                    </template>
                    <template #icon>
                        <common-toggle-visibility-button
                            @visibility-toggle-switch="showPassword"
                        ></common-toggle-visibility-button>
                    </template>
                </text-field>
            </form-cell>
        </form-row>

        <form-row>
            <form-cell>
                <text-field
                    v-if="!shouldShowPassword"
                    :label="$t('forms.auth.reset_password.fields.confirm_password.label')"
                    :isRequired="true"
                    :formData="data"
                    valuePath="confirmPassword"
                    :type="confirmPasswordFieldType"
                    :error="confirmPasswordError"
                    @change="change($event)"
                >
                    <template #error="{ error }">
                        <p v-if="error">{{ error }}</p>
                    </template>

                    <template #icon>
                        <common-toggle-visibility-button
                            @visibility-toggle-switch="toggleConfirmPasswordVisibility"
                        ></common-toggle-visibility-button>
                    </template>
                </text-field>
            </form-cell>
        </form-row>

        <form-row>
            <form-cell>
                <checkbox-field :formData="data" valuePath="agree" @change="change($event)">
                    <template #label>
                        <span v-html="$t('forms.auth.register.fields.agree.label')"></span>
                    </template>
                </checkbox-field>
            </form-cell>
        </form-row>

        <template #footer>
            <common-buttons>
                <common-button
                    :isProcessing="isSending"
                    :label="$t('forms.auth.reset_password.microcopy.reset_password')"
                    :isDisabled="!data.agree"
                    type="primary"
                ></common-button>
            </common-buttons>
        </template>
    </form-wrapper>
</template>

<script>
    import FormMixin from 'mixins/FormMixin'
    import AuthAPI from 'api/AuthAPI'
    import PasswordToggleVisibilityMixin from 'mixins/PasswordToggleVisibilityMixin'

    export default {
        mixins: [FormMixin, PasswordToggleVisibilityMixin],

        data() {
            return {
                inviteError: '',
                notificationMessage: '',
                confirmPasswordError: '',
                confirmPasswordVisible: false,
            }
        },
        computed: {
            confirmPasswordFieldType() {
                return this.confirmPasswordVisible ? 'text' : 'password'
            },
        },
        methods: {
            validatePasswords() {
                if (this.data.password && this.data.confirmPassword) {
                    if (this.data.password === this.data.confirmPassword) {
                        this.confirmPasswordError = ''
                    } else {
                        this.confirmPasswordError = 'Password does not match'
                    }
                } else {
                    this.confirmPasswordError = ''
                }

                if (this.inviteError) {
                    this.inviteError = ''
                }
            },
            toggleConfirmPasswordVisibility() {
                this.confirmPasswordVisible = !this.confirmPasswordVisible
            },

            handleFormSubmission(data) {
                if (!this.shouldShowPassword) {
                    if (data.password !== data.confirmPassword) {
                        this.confirmPasswordError = 'Password does not match'
                        this.inviteError = ''
                        return
                    } else {
                        this.confirmPasswordError = ''
                    }
                }
                this.handleRequest(AuthAPI.setPassword(this.$route.query.e, data.password, this.$route.query.c))
                    .then(() => this.emitEvent({ name: 'sent' }))
                    .catch((err) => {
                        this.data.password = ''
                        this.data.confirmPassword = ''
                        if (err?.response?.code === 'validation_failed') {
                            const fieldError = err.response.data?.[0]
                            if (fieldError?.field === 'password') {
                                this.inviteError = fieldError.message
                            } else {
                                this.inviteError = err.response.message || 'Validation failed'
                            }
                        } else {
                            this.inviteError = 'error'
                        }
                    })
                    .finally(() => {
                        this.isSending = false
                    })
            },

            sendReminder() {
                this.inviteError = ''
                this.handleRequest(AuthAPI.forgotPassword(this.$route.query.e)).then(() => {
                    this.isSending = false
                    this.$router.push({ name: 'auth.login', query: { verified: 'email_sent' } })
                })
            },
        },
        watch: {
            shouldShowPassword(newVal) {
                if (!newVal) {
                    this.confirmPasswordVisible = false
                }
            },
            'data.password'() {
                this.validatePasswords()
            },
            'data.confirmPassword'() {
                this.validatePasswords()
            },
        },
    }
</script>

<style scoped lang="scss">
    .error-button {
        cursor: pointer;
    }
</style>
